import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('funnel-validation');

export type FunnelProgress = {
  currentStepIndex: number;
  funnelSteps: string[];
};

export type ExperimentFunnels = Record<string, FunnelProgress | undefined>;

/**
 * Initialize funnel tracking for an experiment
 *
 * Resets any existing progress and sets up a new funnel with the specified steps.
 * Should be called when a user becomes eligible for an experiment.
 *
 * @param flagName - The LaunchDarkly experiment flag name
 * @param userKey - The user's unique identifier
 * @param funnelSteps - Array of metric names that define the funnel steps in order
 */
export const initializeFunnel = (flagName: string, userKey: string, funnelSteps: string[]): void => {
  try {
    // Validate funnel steps for duplicates
    const uniqueSteps = new Set(funnelSteps);
    if (uniqueSteps.size !== funnelSteps.length) {
      const duplicates = funnelSteps.filter((step, index) => funnelSteps.indexOf(step) !== index);
      throw new Error(
        `Experiment funnel definition contains duplicate steps: ${duplicates.join(
          ', '
        )} in ${flagName}. This may cause tracking issues.`
      );
    }

    const storageKey = getFunnelStorageKey(userKey);
    const data = sessionStorage.getItem(storageKey);
    const funnels: ExperimentFunnels = data ? JSON.parse(data) : {};

    funnels[flagName] = {
      currentStepIndex: 0,
      funnelSteps,
    };

    sessionStorage.setItem(storageKey, JSON.stringify(funnels));
  } catch (error) {
    logger.error('Error initializing funnel:', error);
  }
};

/**
 * Track a metric with automatic funnel validation
 *
 * Automatically detects which experiment the metric belongs to, validates that it's the next
 * expected step in the funnel, and tracks the metric only if validation passes. Updates
 * funnel progress and cleans up completed funnels automatically.
 *
 * @param metric - The metric name to track (must match a step in an active funnel)
 * @param userKey - The user's unique identifier
 * @param trackFn - Function to call for actual metric tracking (e.g., client.trackMetric)
 * @param value - Optional numeric value to associate with the metric
 */
export const trackMetricWithFunnelValidation = (
  metric: string,
  userKey: string,
  trackFn: (metric: string, value?: number) => void,
  value?: number
): void => {
  // Automatically detect which experiment this metric belongs to
  const experimentFlagName = findExperimentForMetric(metric, userKey);
  if (!experimentFlagName) {
    return;
  }

  // Check if user can track this step in the funnel
  if (canTrackStep(experimentFlagName, metric, userKey)) {
    trackFn(metric, value); // Call the provided tracking function
    updateFunnelProgress(experimentFlagName, userKey);
  }
};

/**
 * Get current funnel progress for a specific experiment
 *
 * Returns the user's current position and step definitions for the specified experiment.
 * Useful for displaying progress indicators, checking funnel state, or conditional UI logic.
 *
 * @param experimentFlagName - The LaunchDarkly experiment flag name
 * @param userKey - The user's unique identifier
 * @returns The current funnel progress or null if no progress exists
 */
export const getFunnelProgress = (experimentFlagName: string, userKey: string): FunnelProgress | null => {
  try {
    const data = sessionStorage.getItem(getFunnelStorageKey(userKey));
    if (!data) {
      return null;
    }

    const funnels: ExperimentFunnels = JSON.parse(data);
    return funnels[experimentFlagName] ?? null;
  } catch (error) {
    return null;
  }
};

// Private functions

const getFunnelStorageKey = (userKey: string): string => `experiment-funnels-${userKey}`;

/**
 * Check if user can track a specific metric step (internal use only)
 */
const canTrackStep = (experimentFlagName: string, metric: string, userKey: string): boolean => {
  const progress = getFunnelProgress(experimentFlagName, userKey);
  // Must have funnel progress initialized
  if (!progress) {
    return false;
  }
  // Check if metric is part of the funnel
  const metricIndex = progress.funnelSteps.indexOf(metric);
  if (metricIndex === -1) {
    return false;
  }
  // Check if this is the next expected step
  return metricIndex === progress.currentStepIndex;
};

/**
 * Update funnel progress when a step is completed (internal use only)
 */
const updateFunnelProgress = (experimentFlagName: string, userKey: string): void => {
  try {
    const storageKey = getFunnelStorageKey(userKey);
    const data = sessionStorage.getItem(storageKey);
    const funnels: ExperimentFunnels = data ? JSON.parse(data) : {};

    const funnelData = funnels[experimentFlagName];
    if (!funnelData) {
      return;
    }
    // Advance to next step
    funnelData.currentStepIndex += 1;
    // Check if funnel is complete and clean up if so
    if (funnelData.currentStepIndex >= funnelData.funnelSteps.length) {
      const updatedFunnels = Object.fromEntries(Object.entries(funnels).filter(([key]) => key !== experimentFlagName));
      sessionStorage.setItem(storageKey, JSON.stringify(updatedFunnels));
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify(funnels));
  } catch (error) {
    logger.error('Error updating funnel progress:', error);
  }
};

/**
 * Get all funnel data for a user, the funnel is defined in sessionStorage (internal use only)
 */
const getAllFunnelData = (userKey: string): ExperimentFunnels | null => {
  try {
    const data = sessionStorage.getItem(getFunnelStorageKey(userKey));
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading all funnel data:', error);
    return null;
  }
};

/**
 * Find which experiment a metric belongs to by scanning all active funnel data
 */
const findExperimentForMetric = (metric: string, userKey: string): string | null => {
  const allFunnelData = getAllFunnelData(userKey);
  if (!allFunnelData) {
    return null;
  }

  // Scan through all experiments to find which one contains this metric
  const experimentEntry = Object.entries(allFunnelData).find(([, funnelProgress]) =>
    funnelProgress?.funnelSteps.includes(metric)
  );

  return experimentEntry ? experimentEntry[0] : null;
};
